import React, { useEffect, useState } from "react";
import { Table, Button, UncontrolledTooltip } from "reactstrap";
import { withNamespaces } from "react-i18next";
import { RisksRates } from "src/modules/3rd-party-management/constants";
import { Accordion } from "react-bootstrap";
import ShareholderImage from "src/modules/3rd-party-management/assets/images/shareholders.svg";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import KYBService from "src/modules/3rd-party-management/apis/admin/KYBService";
import { EMPTY_LIST, INTERNATIONAL_DATE_FORMAT } from "src/common/constants";
import DateUtils from "src/services/utils/DateUtils";
import { formatEntityTypeLabel } from "src/modules/3rd-party-management/utils/shareholderGraph";

function extractTeam(managersData) {
    if (!managersData || !Array.isArray(managersData.shareholders)) return [];

    const results = [];
    let index = 0;

    for (const manager of managersData.shareholders) {
        const name = manager.name || "—";
        const type = manager.type;
        const percentage =
            typeof manager.calculated_percentage === "number"
                ? manager.calculated_percentage.toFixed(2)
                : null;
        const risk = manager.risk;
        const hasFindings = manager.hasFindings || false;
        const addr = manager.address
            ? `${manager.address.city || ""}, ${manager.address.country || ""}`
            : null;

        results.push({
            id: manager.cin || index++,
            name,
            type,
            percentage,
            risk,
            address: addr,
            hasFindings,
            isScreened: true,
            complete: manager.complete,
            maxDepth: manager.max_depth,
            minDepth: manager.min_depth,
        });
    }

    return results;
}

const UltimateShareholders = ({
    t,
    activeAccordion,
    setActiveAccordion,
    detailsQuery,
}) => {
    const idInSource = detailsQuery.data?.company?.idInSource;
    const [team, setTeam] = useState([]);
    const dateUtils = new DateUtils();
    const history = useHistory();

    const {
        data: shareholders,
        isFetching: fetchingHolders,
        ...shareHoldersQuery
    } = useQuery({
        queryKey: [
            "3rd-party-management-kyb-get-organization-details",
            "ultimate-shareholder",
            idInSource,
        ],
        queryFn: async () => {
            const service = KYBService.getInstance();

            return await service.getUltimateShareholders(idInSource, {});
        },
        cacheTime: 10,
        refetchOnWindowFocus: false,
        enabled: !!idInSource,
        retry: 0,
    });

    useEffect(() => {
        if (shareholders) {
            setTeam(extractTeam(shareholders));
        }
    }, [shareholders]);

    const formattedUpdateDate = shareholders?.update_date
        ? dateUtils.convertDateToDate(
              shareholders.update_date,
              INTERNATIONAL_DATE_FORMAT
          )
        : null;

    return (
        <Accordion.Item
            onClick={() => setActiveAccordion("ultimate-shareholders")}
            className={activeAccordion === "ultimate-shareholders" ? "active" : ""}
            eventKey="ultimate-shareholders"
        >
            <Accordion.Header>
                <img src={ShareholderImage} alt="" />
                <span>{t("Ultimate Shareholders")}</span>
            </Accordion.Header>

            <Accordion.Body>
                <div className="shareholders-section mb-5">
                    {!fetchingHolders && shareholders && (
                        <div className="mb-3 d-flex flex-column gap-2">
                            <div>
                                <strong className="me-1">
                                    {t("Total ultimate shareholders")}:
                                </strong>
                                {typeof shareholders.amount_ultimate_shareholders ===
                                "number"
                                    ? shareholders.amount_ultimate_shareholders
                                    : "—"}
                            </div>
                            <div>
                                <strong className="me-1">{t("Update Date")}:</strong>
                                {formattedUpdateDate || "—"}
                            </div>
                        </div>
                    )}

                    <Table striped size="sm" aria-label="Shareholders table">
                        <caption className="visually-hidden">
                            {t("List of shareholders related to this organization.")}
                        </caption>
                        <thead
                            style={{
                                position: "sticky",
                                top: 0,
                                background: "white",
                                zIndex: 2,
                            }}
                        >
                            <tr>
                                <th scope="col">{t("Name")}</th>
                                <th scope="col">{t("Type")}</th>
                                <th scope="col">{t("Address")}</th>
                                <th scope="col">{t("Percentage")}</th>
                                <th scope="col">{t("Findings")}</th>
                                <th scope="col">
                                    {t("Compliance Check")}
                                    <span
                                        id="compliance-screening-info"
                                        className="ms-1 text-muted"
                                        role="button"
                                        tabIndex="0"
                                    >
                                        <i
                                            className="ri-information-line"
                                            aria-hidden="true"
                                        />
                                        <span className="visually-hidden">
                                            {t("Information about compliance check")}
                                        </span>
                                    </span>
                                    <UncontrolledTooltip
                                        placement="top"
                                        target="compliance-screening-info"
                                    >
                                        {t("Compliance check data is provided by")}{" "}
                                        <strong>Company.info</strong>.
                                    </UncontrolledTooltip>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {fetchingHolders ? (
                                <tr>
                                    <td colSpan={6} className="text-center">
                                        {t("Fetching")}...
                                    </td>
                                </tr>
                            ) : team.length > 0 ? (
                                team.map((s) => (
                                    <tr key={s.id}>
                                        <td>
                                            <i
                                                className="ri-checkbox-blank-circle-fill font-size-18 align-middle me-1"
                                                style={{
                                                    color: RisksRates[s.risk]?.color,
                                                }}
                                            />
                                            {s.name}
                                        </td>
                                        <td>
                                            {formatEntityTypeLabel(s.type, t) || "—"}
                                        </td>
                                        <td>{s.address || "—"}</td>
                                        <td>
                                            {s.percentage ? `${s.percentage}%` : "—"}
                                        </td>
                                        <td>
                                            {s.hasFindings ? (
                                                <Button
                                                    color="link"
                                                    className="p-0"
                                                    onClick={() => {
                                                        history.push({
                                                            search: "?tab=findings",
                                                        });
                                                    }}
                                                >
                                                    <i className="ri-links-line align-middle me-1" />
                                                    {t("View")}
                                                </Button>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td>
                                            <span>
                                                {s.isScreened ? (
                                                    <i className="ri-checkbox-circle-line font-size-20 text-success me-1 align-middle" />
                                                ) : (
                                                    <i className="ri-close-circle-line font-size-20 text-danger me-1 align-middle" />
                                                )}
                                            </span>
                                            {!s.isScreened && (
                                                <Button
                                                    size="sm"
                                                    outline
                                                    color={
                                                        s.isScreened
                                                            ? "secondary"
                                                            : "success"
                                                    }
                                                    aria-label={`Toggle screening status for ${s.name}`}
                                                >
                                                    {t("Mark as Passed")}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td className="text-center" colSpan={6}>
                                        {shareHoldersQuery.isError ? (
                                            <span className="text-danger">
                                                {t(
                                                    "Error fetching ultimate shareholder data."
                                                )}
                                            </span>
                                        ) : (
                                            t(EMPTY_LIST)
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </Accordion.Body>
        </Accordion.Item>
    );
};

export default withNamespaces()(UltimateShareholders);
